/**
 * Responsive Design Tokens for Parent Portal Refactor
 * 
 * CREATED FOR: Parent Portal refactor & redesign (Phase 1.1)
 * PURPOSE: Standardize responsive design patterns across Parent Portal
 * TECH DEBT: Eliminates inconsistent spacing, typography, and touch targets
 * 
 * This file provides consistent design tokens that:
 * - Ensure proper touch targets (min 44px) for mobile accessibility
 * - Standardize spacing patterns across mobile/desktop
 * - Provide consistent typography scaling
 * - Define animation patterns for responsive interactions
 */

import { BREAKPOINTS } from '../hooks/useResponsive';

/**
 * Touch Target Standards
 * Based on Apple HIG, Material Design, and WCAG 2.1 AA guidelines
 */
export const TOUCH_TARGETS = {
  // WCAG 2.1 AA minimum touch target size (44px minimum)
  minSize: '44px',
  // Recommended touch target size for comfort (48px)
  comfortable: '48px',
  // Large touch targets for primary actions (56px)
  large: '56px',
  // WCAG-compliant minimum dimensions
  wcagMinWidth: '44px',
  wcagMinHeight: '44px',
  // Spacing between touch targets (WCAG recommendation)
  spacing: '8px',
  // Padding for touch-friendly elements
  padding: {
    sm: '8px 12px',
    md: '12px 16px', 
    lg: '16px 24px'
  }
} as const;

/**
 * Responsive Spacing System
 * Provides consistent spacing that scales appropriately across devices
 */
export const RESPONSIVE_SPACING = {
  // Container padding - responsive padding for main content areas
  container: {
    mobile: 'px-4',      // 16px padding on mobile
    tablet: 'md:px-6',   // 24px padding on tablet+
    desktop: 'lg:px-8'   // 32px padding on desktop+
  },
  
  // Section spacing - vertical spacing between major sections
  section: {
    mobile: 'space-y-4',      // 16px spacing on mobile
    tablet: 'md:space-y-6',   // 24px spacing on tablet+
    desktop: 'lg:space-y-8'   // 32px spacing on desktop+
  },
  
  // Card spacing - spacing between cards and within cards
  card: {
    gap: 'gap-4 md:gap-6',           // Gap between cards
    padding: 'p-4 md:p-6',           // Internal card padding
    margin: 'mb-4 md:mb-6'           // Margin below cards
  },
  
  // List spacing - spacing for list items and navigation
  list: {
    gap: 'gap-2 md:gap-3',           // Gap between list items
    padding: 'py-2 px-3 md:py-3 md:px-4'  // List item padding
  }
} as const;

/**
 * Responsive Typography System
 * Ensures readable text across all device sizes
 */
export const RESPONSIVE_TYPOGRAPHY = {
  // Page headings
  h1: 'text-2xl md:text-3xl lg:text-4xl font-bold',
  h2: 'text-xl md:text-2xl lg:text-3xl font-semibold',
  h3: 'text-lg md:text-xl lg:text-2xl font-semibold',
  h4: 'text-base md:text-lg font-medium',
  
  // Body text
  body: 'text-sm md:text-base',
  bodyLarge: 'text-base md:text-lg',
  bodySmall: 'text-xs md:text-sm',
  
  // UI elements
  button: 'text-sm md:text-base font-medium',
  buttonLarge: 'text-base md:text-lg font-medium',
  label: 'text-xs md:text-sm font-medium',
  caption: 'text-xs text-muted-foreground',
  
  // Special cases
  cardTitle: 'text-lg md:text-xl font-semibold',
  cardSubtitle: 'text-sm md:text-base text-muted-foreground',
  navItem: 'text-sm md:text-base font-medium'
} as const;

/**
 * Responsive Grid Systems
 * Consistent grid patterns for different content types
 */
export const RESPONSIVE_GRIDS = {
  // Dashboard cards grid
  dashboard: 'grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
  
  // Student cards grid
  students: 'grid gap-4 md:gap-6 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3',
  
  // Class cards grid
  classes: 'grid gap-4 md:gap-6 grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  
  // Two column layout (form + preview, etc.)
  twoColumn: 'grid gap-6 lg:gap-8 grid-cols-1 lg:grid-cols-2',
  
  // Three column layout (sidebar + main + aside)
  threeColumn: 'grid gap-6 grid-cols-1 lg:grid-cols-[250px_1fr] xl:grid-cols-[250px_1fr_300px]'
} as const;

/**
 * Animation Patterns for Responsive Interactions
 * Consistent animation patterns across mobile and desktop
 */
export const RESPONSIVE_ANIMATIONS = {
  // Card interactions
  cardHover: {
    mobile: 'active:scale-[0.98] transition-transform duration-150',
    desktop: 'hover:scale-[1.02] hover:-translate-y-1 transition-all duration-200'
  },
  
  // Button interactions
  buttonPress: {
    mobile: 'active:scale-95 transition-transform duration-100',
    desktop: 'hover:scale-105 active:scale-95 transition-transform duration-150'
  },
  
  // List item interactions
  listItem: {
    mobile: 'active:bg-accent/50 transition-colors duration-150',
    desktop: 'hover:bg-accent/30 active:bg-accent/50 transition-colors duration-150'
  },
  
  // Page transitions
  pageTransition: 'transition-all duration-300 ease-in-out',
  
  // Loading states
  skeleton: 'animate-pulse',
  spinner: 'animate-spin'
} as const;

/**
 * Container Classes for Responsive Layouts
 * Pre-defined container classes for common layout patterns
 */
export const RESPONSIVE_CONTAINERS = {
  // Page container - main content wrapper
  page: 'min-h-screen bg-background',
  
  // Content container - content area with proper max-width and centering
  content: 'container mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8',
  
  // Card container - consistent card wrapper
  card: 'bg-card border rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200',
  
  // Modal container - responsive modal sizing
  modal: 'w-full max-w-sm md:max-w-md lg:max-w-lg mx-auto',
  
  // Form container - proper form layout
  form: 'space-y-4 md:space-y-6',
  
  // Navigation container - responsive nav wrapper
  nav: 'sticky top-0 z-40 bg-background/80 backdrop-blur-sm border-b'
} as const;

/**
 * Utility function to combine responsive classes
 */
export const combineResponsiveClasses = (...classes: string[]): string => {
  return classes.filter(Boolean).join(' ');
};

/**
 * Utility function to get responsive class based on breakpoint
 */
export const getResponsiveClass = (
  mobile: string,
  desktop?: string,
  tablet?: string
): string => {
  const classes = [mobile];
  if (tablet) classes.push(`md:${tablet}`);
  if (desktop) classes.push(`lg:${desktop}`);
  return classes.join(' ');
};

export default {
  BREAKPOINTS,
  TOUCH_TARGETS,
  RESPONSIVE_SPACING,
  RESPONSIVE_TYPOGRAPHY,
  RESPONSIVE_GRIDS,
  RESPONSIVE_ANIMATIONS,
  RESPONSIVE_CONTAINERS,
  combineResponsiveClasses,
  getResponsiveClass
};